import {
	ReactElement,
	Ref,
	useCallback,
	useState
} from "react";
import { ExchangeEmailId } from "client/Api/Email/common";
import {
	EmailAddressModel,
	MessageFlagState,
	MessageReadState
} from "client/Api/Email/email";
import {
	acceptMeetingInvitation,
	declineMeetingInvitation,
} from "client/Api/Email/respondToEmail";

import { localize } from "client_legacy/ndaw/culture";
import {
	EmailIdWithIsReadStatus
} from "client_legacy/Email/emailEvents";
import {
	EmailSessionState,
	EmailSessionStateKind,
	EmailResponseSessionState,
	EmailResponseKind,
	ViewEmailSessionState,
	CreateNewEmailSessionState,
	EditDraftEmailSessionState
} from "../EmailPage/EmailPage";
import { AlertManager, useAlerts } from "client/Hooks";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { FontStyleModel } from "client/Api/common";
import { ContainerWithBusyIndicator, NoResults } from "client/Components";
import styles from "./EmailContent.module.less";
import { EmailViewer } from "./EmailViewer/EmailViewer";
import { EmailActionModel, EmailSelectionActionContext } from "../Actions/selectionActions";
import { EmailComposer, EmailComposerHandle } from "./EmailComposer/EmailComposer";
import { never } from "client_legacy/ndaw/errors";
import { SelectionMenu } from "./SelectionMenu/SelectionMenu";
import { TimeZone } from "client/DateAndTime/timezones";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type EmailContentProps = {
	smtpAddress: string | null;
	emailSession: EmailSessionState;
	defaultComposerFontStyle: FontStyleModel;
	shouldAutomaticallyShowSenderImages: boolean;
	availableFroms: readonly EmailAddressModel[];
	onSwitchToNoSession(): void;
	onOpenEmail(emailId: string, isForEditing: boolean): void;
	onOpenFolder: (smtpAddress: string | null, folderId: string) => void;
	onDeleteEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onDeleteSingleEmail(
		emailId: ExchangeEmailId,
		smtpAddress: string | null,
		parentFolderId: string | null
	): void;
	onNotifyAboutDeletedEmails(emails: readonly EmailIdWithIsReadStatus[]): void;
	onNotifyAboutCanceledEmail(): void;
	afterSendResponse(response: EmailResponseSessionState): void;
	shouldShowEmptyMessage: boolean;
	onRespondToEmail(
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	): void;
	onFlagStatusChange(
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void;
	onReadStatusChange(
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void;
	onMoveEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onClearSelection(): void;
	selectionContext: EmailSelectionActionContext;
	onEmailBeingViewedChange(email: EmailActionModel | null): void;
	isAttachmentUploadInProgress: boolean;
	onIsAttachmentUploadInProgressChange(isAttachmentUploadInProgress: boolean): void;
	composerRef: Ref<EmailComposerHandle>;
	timezone: TimeZone;
};

export const EmailContent = (props: EmailContentProps) => {
	const {
		smtpAddress,
		emailSession,
		defaultComposerFontStyle,
		availableFroms,
		onDeleteEmails,
		onDeleteSingleEmail,
		onNotifyAboutDeletedEmails,
		onNotifyAboutCanceledEmail,
		onSwitchToNoSession,
		onOpenEmail,
		onOpenFolder,
		afterSendResponse,
		shouldShowEmptyMessage,
		shouldAutomaticallyShowSenderImages,
		onRespondToEmail,
		onFlagStatusChange,
		onReadStatusChange,
		onMoveEmails,
		onClearSelection,
		onEmailBeingViewedChange,
		selectionContext,
		isAttachmentUploadInProgress,
		onIsAttachmentUploadInProgressChange,
		composerRef,
		timezone
	} = props;

	const renderContent = (): ReactElement | null => {
		if (selectionContext.effectiveSelection.length > 1) {
			return (
				<div className={styles.scrollContainer}>
					<SelectionMenu
						onClearSelection={onClearSelection}
						selectionContext={selectionContext}
						isAttachmentUploadInProgress={isAttachmentUploadInProgress}
						onMoveEmails={onMoveEmails}
						onReadStatusChange={onReadStatusChange}
						onFlagStatusChange={onFlagStatusChange}
						onRespondToEmail={onRespondToEmail}
						onDeleteEmails={onDeleteEmails}
					/>
				</div>
			);
		}

		switch (emailSession.kind) {
			case EmailSessionStateKind.ViewEmail:
				return (
					<Viewer
						sessionState={emailSession}
						shouldAutomaticallyShowSenderImages={shouldAutomaticallyShowSenderImages}
						onOpenFolder={onOpenFolder}
						onRespondToEmail={onRespondToEmail}
						onFlagStatusChange={onFlagStatusChange}
						onReadStatusChange={onReadStatusChange}
						onDeleteEmails={onDeleteEmails}
						onMoveEmails={onMoveEmails}
						onEmailBeingViewedChange={onEmailBeingViewedChange}
						timezone={timezone}
					/>
				);
			case EmailSessionStateKind.NewEmail:
			case EmailSessionStateKind.EditDraft:
				return (
					<Composer
						smtpAddress={smtpAddress}
						session={emailSession}
						defaultComposerFontStyle={defaultComposerFontStyle}
						availableFroms={availableFroms}
						composerRef={composerRef}
						onSwitchToNoSession={onSwitchToNoSession}
						onOpenEmail={onOpenEmail}
						afterSendResponse={afterSendResponse}
						onNotifyAboutDeletedEmails={onNotifyAboutDeletedEmails}
						onNotifyAboutCanceledEmail={onNotifyAboutCanceledEmail}
						onDeleteSingleEmail={onDeleteSingleEmail}
						onIsAttachmentUploadInProgressChange={onIsAttachmentUploadInProgressChange}
					/>
				);
			case EmailSessionStateKind.None:
				return !shouldShowEmptyMessage
					? null
					: (
						<div className={styles.scrollContainer}>
							<div className={styles.empty}>
								<NoResults
									icon="envelope"
									title={localize("_NoEmailSelected")}
									message={localize("_ThereIsCurrentlyNoEmailSelected")}
									height="fill"
								/>
							</div>
						</div>
					);
			default:
				never(emailSession);
		}
	};

	return (
		<div
			{...setTrackingId("email-content")}
			className={styles.root}
		>
			{renderContent()}
		</div>
	);
};

EmailContent.displayName = "EmailContent";

type ViewerProps = {
	sessionState: ViewEmailSessionState;
	shouldAutomaticallyShowSenderImages: boolean;
	onOpenFolder: (smtpAddress: string | null, folderId: string) => void;
	onRespondToEmail: (
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	) => void;
	onFlagStatusChange: (
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	) => void;
	onReadStatusChange: (
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	) => void;
	onDeleteEmails: (emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null) => void;
	onMoveEmails: (emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null) => void;
	onEmailBeingViewedChange: (email: EmailActionModel | null) => void;
	timezone: TimeZone;
};

const Viewer = (props: ViewerProps): ReactElement => {
	const {
		sessionState,
		shouldAutomaticallyShowSenderImages,
		onOpenFolder,
		onRespondToEmail,
		onFlagStatusChange,
		onReadStatusChange,
		onDeleteEmails,
		onMoveEmails,
		onEmailBeingViewedChange,
		timezone
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();

	const acceptMeetingInviteHandler = useCallback(
		(
			emailId: ExchangeEmailId,
			isResponseRequested: boolean,
			isMessageRead: boolean
		): void => {
			acceptMeetingInvitation(
				{
					smtpAddress: isNotNil(sessionState.smtpAddress)
						? { isSet: true, value: sessionState.smtpAddress }
						: { isSet: false },
					messageId: emailId.id
				},
				isResponseRequested
			)
				.then(() => {
					onDeleteEmails(
						[{ emailId: emailId, isRead: isMessageRead }],
						sessionState.smtpAddress
					);
				})
				.catch(() =>
					alertManager.showError(
						localize("_FailedToAcceptTheInvitation")
					)
				);
		},
		[
			alertManager,
			onDeleteEmails,
			sessionState.smtpAddress
		]
	);

	const declineMeetingInviteHandler = useCallback(
		(
			emailId: ExchangeEmailId,
			isResponseRequested: boolean,
			isMessageRead: boolean
		) => {
			declineMeetingInvitation(
				{
					smtpAddress: isNotNil(sessionState.smtpAddress)
						? { isSet: true, value: sessionState.smtpAddress }
						: { isSet: false },
					messageId: emailId.id
				},
				isResponseRequested
			)
				.then(() => {
					onDeleteEmails(
						[{ emailId: emailId, isRead: isMessageRead }],
						sessionState.smtpAddress
					);
				})
				.catch(() =>
					alertManager.showError(
						localize("_FailedToDeclineTheInvitation")
					)
				);
		},
		[
			onDeleteEmails,
			alertManager,
			sessionState.smtpAddress
		]
	);

	return (
		<ContainerWithBusyIndicator size="fill" hasBusyIndicator={isBusy}>
			<div className={styles.scrollContainer}>
				<EmailViewer
					sessionState={sessionState}
					onAcceptMeetingInvite={acceptMeetingInviteHandler}
					onDeclineMeetingInvite={declineMeetingInviteHandler}
					onOpenFolder={onOpenFolder}
					shouldAutomaticallyShowSenderImages={shouldAutomaticallyShowSenderImages}
					onRespondToEmail={onRespondToEmail}
					onFlagStatusChange={onFlagStatusChange}
					onReadStatusChange={onReadStatusChange}
					onDeleteEmails={onDeleteEmails}
					onMoveEmails={onMoveEmails}
					onEmailBeingViewedChange={onEmailBeingViewedChange}
					timezone={timezone}
					onIsBusyChange={setIsBusy}
				/>
				<AiAssistantFabSpacing shouldIncludeContentPadding />
			</div>
		</ContainerWithBusyIndicator>
	);
};

Viewer.displayName = "Viewer";

type ComposerProps = {
	smtpAddress: string | null;
	session: CreateNewEmailSessionState | EditDraftEmailSessionState;
	defaultComposerFontStyle: FontStyleModel;
	availableFroms: readonly EmailAddressModel[];
	composerRef: Ref<EmailComposerHandle>;
	onSwitchToNoSession: () => void;
	onOpenEmail: (emailId: string, isForEditing: boolean) => void;
	afterSendResponse: (response: EmailResponseSessionState) => void;
	onNotifyAboutDeletedEmails: (emails: readonly EmailIdWithIsReadStatus[]) => void;
	onNotifyAboutCanceledEmail: () => void;
	onDeleteSingleEmail: (
		emailId: ExchangeEmailId,
		smtpAddress: string | null,
		parentFolderId: string | null
	) => void;
	onIsAttachmentUploadInProgressChange: (isAttachmentUploadInProgress: boolean) => void;
};

const Composer = (props: ComposerProps): ReactElement => {
	const {
		smtpAddress,
		session,
		defaultComposerFontStyle,
		availableFroms,
		composerRef,
		onSwitchToNoSession,
		onOpenEmail,
		afterSendResponse,
		onNotifyAboutDeletedEmails,
		onNotifyAboutCanceledEmail,
		onDeleteSingleEmail,
		onIsAttachmentUploadInProgressChange
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const sentHandler = useCallback(
		(emailId: ExchangeEmailId, response: EmailResponseSessionState | null): void => {
			if (isNotNil(response)) {
				afterSendResponse(response);
				onOpenEmail(response.originalEmailId.id, false);
			} else {
				onSwitchToNoSession();
			}

			onNotifyAboutDeletedEmails(
				[
					{
						emailId,
						isRead: false
					}
				]
			);
		},
		[
			afterSendResponse,
			onNotifyAboutDeletedEmails,
			onSwitchToNoSession,
			onOpenEmail
		]
	);

	const deleteHandler = useCallback(
		(emailId: ExchangeEmailId, parentFolderId: string | null): void => {
			onNotifyAboutCanceledEmail();

			onDeleteSingleEmail(
				emailId,
				session.kind === EmailSessionStateKind.EditDraft
					? session.smtpAddress
					: smtpAddress,
				parentFolderId
			);
		},
		[
			session,
			smtpAddress,
			onDeleteSingleEmail,
			onNotifyAboutCanceledEmail
		]
	);

	return (
		<ContainerWithBusyIndicator size="fill" hasBusyIndicator={isBusy}>
			<div className={styles.scrollContainer}>
				<EmailComposer
					defaultComposerFontStyle={defaultComposerFontStyle}
					ref={composerRef}
					sessionState={session}
					smtpAddress={smtpAddress}
					fromAddressOptions={availableFroms}
					onEmailSent={sentHandler}
					onDeleteEmail={deleteHandler}
					onDraftSaved={onNotifyAboutCanceledEmail}
					onHasUploadingAttachmentsChange={onIsAttachmentUploadInProgressChange}
					onIsBusyChange={setIsBusy}
				/>
				<AiAssistantFabSpacing shouldIncludeContentPadding />
			</div>
		</ContainerWithBusyIndicator>
	);
};

Composer.displayName = "Composer";